import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { EQUIPMENT, SERVICES, SITE } from '../data/site';
import { Seo } from '../lib/seo';
import { Reveal, SpotlightImage } from '../components/motion';
import { CTABand, Breadcrumbs, PageHero } from '../components/chrome';
import { RFQForm } from '../components/RFQForm';

export default function EquipmentCategory() {
  const { slug } = useParams();
  const eq = EQUIPMENT.find((e) => e.slug === slug);
  if (!eq) return <Navigate to="/equipment" replace />;

  const idx = EQUIPMENT.indexOf(eq);
  const crumbs = [{ label: 'Home', href: '/' }, { label: 'Equipment', href: '/equipment' }, { label: eq.name }];
  const productLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `${eq.name} — Supply, Installation & Support`,
    serviceType: 'Analytical equipment supply, installation, and lifecycle support',
    provider: { '@type': 'Organization', name: SITE.name, url: SITE.url },
    areaServed: 'United States (nationwide)',
    description: eq.meta.desc,
  };

  return (
    <div data-testid={`page-equipment-${eq.slug}`}>
      <Seo title={eq.meta.title} description={eq.meta.desc} path={`/equipment/${eq.slug}`} ld={[productLd]} breadcrumbs={crumbs} />
      <PageHero
        eyebrow={`Equipment · ${String(idx + 1).padStart(2, '0')} / ${String(EQUIPMENT.length).padStart(2, '0')}`}
        lines={eq.name}
        sub={eq.tagline}
        breadcrumbs={crumbs}
        image={eq.image}
        imageAlt={eq.alt}
      />

      <section className="section-pad" data-testid="equipment-category-body">
        <div className="container-x grid lg:grid-cols-[1.1fr_0.9fr] gap-12">
          <div>
            <Reveal>
              <h2 className="display text-2xl sm:text-3xl text-ink">Supplied. Installed. Supported.</h2>
              <p className="mt-5 text-slate-600 leading-relaxed">{eq.intro}</p>
            </Reveal>
            <Reveal delay={0.08}>
              <h3 className="eyebrow text-signal mt-10 mb-4">What we supply in this category</h3>
              <ul className="border-t border-slate-200">
                {eq.items.map((it) => (
                  <li key={it} className="flex items-start gap-3 py-3 border-b border-slate-200 text-slate-700">
                    <Check size={16} className="text-signal mt-0.5 shrink-0" /> {it}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.1}>
              <h3 className="eyebrow text-signal mt-10 mb-4">Pair this equipment with</h3>
              <div className="flex flex-wrap gap-2">
                {['/services/equipment-installation', '/services/system-commissioning', '/services/iq-oq-pq-validation-support', '/services/preventive-maintenance'].map((href) => {
                  const s = SERVICES.find((x) => `/services/${x.slug}` === href);
                  return (
                    <Link key={href} to={href} className="border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:border-signal hover:text-signal transition-colors" data-testid={`equipment-related-service-${s.slug}`}>
                      {s.name}
                    </Link>
                  );
                })}
              </div>
            </Reveal>
          </div>
          <div>
            <Reveal delay={0.05}><SpotlightImage src={eq.image} alt={eq.alt} ratio="aspect-[4/3]" /></Reveal>
            <Reveal delay={0.1}>
              <div className="bg-ink text-white p-7 mt-8" data-testid="equipment-category-cta-card">
                <p className="eyebrow text-signal">Request this equipment</p>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                  Send the requirement — model, quantity, and delivery date if known. Quotes include procurement-ready
                  documentation; GPC, PO, and ACH accepted.
                </p>
                <a href={`mailto:${SITE.email}?subject=${encodeURIComponent('Equipment inquiry: ' + eq.name)}`} className="btn-light w-full mt-5" data-testid="equipment-category-quote-button">Request a Quote</a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section-pad bg-slate-50 hairline-t" data-testid="equipment-category-rfq">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow text-signal mb-3">RFQ</p>
            <h2 className="display text-3xl sm:text-4xl text-ink mb-8">Submit this requirement</h2>
          </Reveal>
          <RFQForm defaultNeed={eq.name} />
        </div>
      </section>

      <section className="section-pad hairline-t" data-testid="equipment-category-related">
        <div className="container-x">
          <h2 className="eyebrow text-signal mb-6">Other equipment categories</h2>
          <div className="flex flex-wrap gap-2">
            {EQUIPMENT.filter((e) => e.slug !== eq.slug).map((e) => (
              <Link key={e.slug} to={`/equipment/${e.slug}`} className="border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 hover:text-signal hover:border-signal transition-colors" data-testid={`equipment-related-${e.slug}`}>
                {e.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
